<template>
    <div>
        <!-- Badge/Button to toggle the numbering check panel -->
        <v-menu
            v-if="!showFullAlert"
            offset-y
            max-width="500"
            :close-on-content-click="false"
        >
            <template v-slot:activator="{ on, attrs }">
                <v-badge
                    :content="loading ? '...' : (sectionalGapsData.length > 0 ? totalGapsCount : '✓')"
                    :color="loading ? 'info' : (sectionalGapsData.length > 0 ? 'warning' : 'success')"
                    overlap
                    class="ma-2"
                >
                    <v-btn
                        small
                        outlined
                        :color="loading ? 'info' : (sectionalGapsData.length > 0 ? 'warning' : 'success')"
                        :loading="loading"
                        v-bind="attrs"
                        v-on="on"
                    >
                        <v-icon small left>mdi-numeric</v-icon>
                        Controllo numerazione
                    </v-btn>
                </v-badge>
            </template>

            <!-- Menu content with year selector and gaps info -->
            <v-card>
                <v-card-text class="pb-2 pt-2">
                    <div class="d-flex align-center mb-2">
                        <v-icon small class="mr-2">mdi-calendar</v-icon>
                        <span class="text-body-2 mr-2">Anno:</span>
                        <v-select
                            v-model="selectedYear"
                            :items="availableYears"
                            dense
                            outlined
                            hide-details
						    :menu-props="{'offset-y': true}"
                            style="max-width: 100px"
                        ></v-select>
                    </div>

                    <!-- Loading state -->
                    <div v-if="loading">
                        <v-divider class="mb-2"></v-divider>
                        <div class="text-center py-3">
                            <v-progress-circular
                                indeterminate
                                color="primary"
                                size="24"
                                width="2"
                                class="mb-2"
                            ></v-progress-circular>
                            <div class="text-body-2">Controllo numerazione in corso...</div>
                            <div class="caption grey--text">Anno {{ selectedYear }}</div>
                        </div>
                    </div>

                    <!-- Gaps found -->
                    <div v-else-if="sectionalGapsData.length > 0">
                        <v-divider class="mb-2"></v-divider>
                        <div class="text-body-2 font-weight-bold mb-2">
                            <v-icon small color="warning" class="mr-1">mdi-alert</v-icon>
                            {{ totalGapsCount }} {{ totalGapsCount === 1 ? 'numero mancante' : 'numeri mancanti' }}
                            su {{ sectionalGapsData.length }} {{ sectionalGapsData.length === 1 ? 'sezionale' : 'sezionali' }}
                        </div>
                        <div
                            v-for="(gap, index) in sectionalGapsData"
                            :key="`gap-${gap.sectionalId}-${selectedYear}`"
                            class="py-2"
                        >
                            <div class="d-flex align-start">
                                <v-icon x-small color="warning" class="mr-2 mt-1">mdi-numeric</v-icon>
                                <div class="flex-grow-1">
                                    <div class="font-weight-medium text-body-2">{{ gap.sectionalName }}</div>
                                    <div class="caption grey--text text--darken-1">
                                        {{ gap.totalMissing }} {{ gap.totalMissing === 1 ? 'numero' : 'numeri' }}
                                        - Intervallo: {{ gap.range }}
                                    </div>
                                    <div v-if="gap.gaps && gap.gaps.length > 0" class="caption mt-1">
                                        <span class="font-weight-bold">Numeri:</span>
                                        <span v-if="gap.gaps.length <= 15">
                                            {{ formatGaps(gap.gaps) }}
                                        </span>
                                        <span v-else>
                                            <span v-if="expandedGaps[`menu-${gap.sectionalId}`]">
                                                {{ formatGaps(gap.gaps) }}
                                            </span>
                                            <span v-else>
                                                {{ formatGaps(gap.gaps.slice(0, 15)) }}
                                                <span class="grey--text">e altri {{ gap.gaps.length - 15 }}...</span>
                                            </span>
                                            <a
                                                href="javascript:void(0)"
                                                class="ml-1 text-decoration-none caption"
                                                @click="toggleGapExpansion(`menu-${gap.sectionalId}`)"
                                            >
                                                {{ expandedGaps[`menu-${gap.sectionalId}`] ? 'Mostra meno' : 'Mostra tutti' }}
                                            </a>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <v-divider v-if="index < sectionalGapsData.length - 1" class="mt-2"></v-divider>
                        </div>
                    </div>

                    <!-- No gaps found -->
                    <div v-else>
                        <v-divider class="mb-2"></v-divider>
                        <div class="text-center py-2">
                            <v-icon color="success" class="mb-1">mdi-check-all</v-icon>
                            <div class="text-body-2 success--text font-weight-medium">
                                Nessun salto di numerazione
                            </div>
                            <div class="caption grey--text">
                                Anno {{ selectedYear }}
                            </div>
                        </div>
                    </div>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-2">
                    <v-spacer></v-spacer>
                    <v-btn
                        outlined
                        small
                        text
                        color="grey"
                        @click="refreshData"
                        :loading="loading"
                    >
                        <v-icon x-small>mdi-refresh</v-icon>
                    </v-btn>
                    <v-btn
                        outlined
                        small
                        text
                        color="primary"
                        @click="showFullAlert = true"
                    >
                        <v-icon small left>mdi-arrow-expand</v-icon>
                        Espandi
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-menu>

        <!-- Full expanded alert -->
        <v-alert
            v-else
            :type="loading ? 'info' : (sectionalGapsData.length > 0 ? 'warning' : 'success')"
            border="left"
            colored-border
            elevation="0"
            class="mb-0 compact-gaps-alert"
        >
            <v-row align="center" no-gutters class="mb-1">
                <v-col cols="12" sm="auto" class="d-flex align-center mb-2 mb-sm-0">
                    <v-progress-circular
                        v-if="loading"
                        indeterminate
                        color="primary"
                        size="20"
                        width="2"
                        class="mr-2"
                    ></v-progress-circular>
                    <v-icon v-else :color="sectionalGapsData.length > 0 ? 'warning' : 'success'" class="mr-2">
                        {{ sectionalGapsData.length > 0 ? 'mdi-alert' : 'mdi-check-all' }}
                    </v-icon>
                    <div>
                        <div class="font-weight-bold text-body-2">
                            <template v-if="loading">
                                Controllo numerazione in corso...
                            </template>
                            <template v-else>
                                {{ sectionalGapsData.length > 0 ? 'Salti di numerazione rilevati' : 'Nessun salto di numerazione' }}
                            </template>
                        </div>
                        <div v-if="loading" class="caption">
                            Anno {{ selectedYear }}
                        </div>
                        <div v-else-if="sectionalGapsData.length > 0" class="caption">
                            {{ totalGapsCount }} {{ totalGapsCount === 1 ? 'numero' : 'numeri' }} su
                            {{ sectionalGapsData.length }} {{ sectionalGapsData.length === 1 ? 'sezionale' : 'sezionali' }}
                        </div>
                        <div v-else class="caption">
                            Anno {{ selectedYear }}
                        </div>
                    </div>
                </v-col>
                <v-col cols="12" sm class="d-flex justify-end align-center">
                    <v-icon small class="mr-2">mdi-calendar</v-icon>
                    <v-select
                        v-model="selectedYear"
                        :items="availableYears"
                        dense
                        outlined
                        hide-details
						:menu-props="{'offset-y': true}"
                        style="max-width: 120px"
                        class="mr-2"
                    ></v-select>
                    <v-btn
                        small
                        icon
                        @click="refreshData"
                        :loading="loading"
                        class="mr-1"
                    >
                        <v-icon small>mdi-refresh</v-icon>
                    </v-btn>
                    <v-btn
                        small
                        icon
                        @click="showFullAlert = false"
                    >
                        <v-icon small>mdi-close</v-icon>
                    </v-btn>
                </v-col>
            </v-row>

            <!-- Accordion with details (only show when there are gaps) -->
            <v-expansion-panels v-if="sectionalGapsData.length > 0" flat class="mt-2">
                <v-expansion-panel>
                    <v-expansion-panel-header class="px-0 py-1 min-height-auto">
                        <template v-slot:default="{ open }">
                            <span class="text-body-2 primary--text font-weight-medium d-flex align-center">
                                <v-icon small color="primary" class="mr-1">
                                    {{ open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                                </v-icon>
                                {{ open ? 'Nascondi dettagli' : 'Mostra dettagli' }}
                            </span>
                        </template>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content class="px-0 pt-2">
                        <v-divider class="mb-2"></v-divider>
                        <div
                            v-for="(gap, index) in sectionalGapsData"
                            :key="`gap-${gap.sectionalId}-${selectedYear}`"
                            class="py-2"
                        >
                            <div class="d-flex align-start">
                                <v-icon small color="warning" class="mr-2">mdi-numeric</v-icon>
                                <div class="flex-grow-1">
                                    <div class="font-weight-medium text-body-2">{{ gap.sectionalName }}</div>
                                    <div class="caption grey--text text--darken-1">
                                        {{ gap.totalMissing }} {{ gap.totalMissing === 1 ? 'numero' : 'numeri' }}
                                        nell'intervallo {{ gap.range }}
                                    </div>
                                    <div v-if="gap.gaps && gap.gaps.length > 0" class="caption mt-1">
                                        <span class="font-weight-bold">Numeri:</span>
                                        <span v-if="gap.gaps.length <= 20">
                                            {{ formatGaps(gap.gaps) }}
                                        </span>
                                        <span v-else>
                                            <span v-if="expandedGaps[`alert-${gap.sectionalId}`]">
                                                {{ formatGaps(gap.gaps) }}
                                            </span>
                                            <span v-else>
                                                {{ formatGaps(gap.gaps.slice(0, 20)) }}
                                                <span class="grey--text">e altri {{ gap.gaps.length - 20 }}...</span>
                                            </span>
                                            <a
                                                href="javascript:void(0)"
                                                class="ml-1 text-decoration-none caption"
                                                @click="toggleGapExpansion(`alert-${gap.sectionalId}`)"
                                            >
                                                {{ expandedGaps[`alert-${gap.sectionalId}`] ? 'Mostra meno' : 'Mostra tutti' }}
                                            </a>
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <v-divider v-if="index < sectionalGapsData.length - 1" class="mt-2"></v-divider>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-alert>
    </div>
</template>

<script>
export default {
    name: 'NumberingGapsContainer',

    props: {
        modelType: {
            type: String,
            required: true
        },
        sectionalIds: {
            type: Array,
            default: null // null means check all sectionals
        }
    },

    data() {
        return {
            allSectionals: [],
            sectionalGapsData: [],
            selectedYear: new Date().getFullYear(),
            loading: false,
            showFullAlert: false,
            // Cache dei risultati per anno
            yearCache: {},
            // Track which sectionals have expanded gap lists
            expandedGaps: {}
        }
    },

    computed: {
        availableYears() {
            const currentYear = new Date().getFullYear()
            const years = []
            // Show current year and 5 previous years
            for (let i = 0; i < 6; i++) {
                years.push(currentYear - i)
            }
            return years
        },

        totalGapsCount() {
            return this.sectionalGapsData.reduce((sum, gap) => sum + gap.totalMissing, 0)
        }
    },

    watch: {
        selectedYear(newYear, oldYear) {
            // Only fetch if year actually changed (not initial mount)
            if (oldYear !== undefined) {
                this.fetchGapsForAllSectionals()
            }
        },

        // Watch for changes in modelType or sectionalIds props
        modelType() {
            this.invalidateCache()
            this.fetchAllSectionals()
        },

        sectionalIds: {
            handler() {
                this.invalidateCache()
                this.fetchGapsForAllSectionals()
            },
            deep: true
        }
    },

    mounted() {
        // Clear cache on mount to ensure fresh data
        this.invalidateCache()
        this.fetchAllSectionals()

        // Listen for document events
        this.$eventBusUtility.$on('documentsDeleted', this.handleDocumentsChanged)
        this.$eventBusUtility.$on('documentCreated', this.handleDocumentsChanged)
        this.$eventBusUtility.$on('documentUpdated', this.handleDocumentsChanged)
    },

    beforeDestroy() {
        // Clean up event listeners
        this.$eventBusUtility.$off('documentsDeleted', this.handleDocumentsChanged)
        this.$eventBusUtility.$off('documentCreated', this.handleDocumentsChanged)
        this.$eventBusUtility.$off('documentUpdated', this.handleDocumentsChanged)
    },

    methods: {
        async fetchAllSectionals() {
            this.loading = true

            try {
                // Fetch all sectionals from API using POST filters endpoint
                const response = await this.$http.post('/sectionals-filters', {
                    page: 1,
                    per_page: 1000, // Get all sectionals
                    sortBy: [],
                    sortDesc: [false],
                    advancedFilters: {},
                    filter: ''
                })

                // The API returns paginated data: response.data.data.data contains the array
                if (response && response.data && response.data.data && Array.isArray(response.data.data.data)) {
                    this.allSectionals = response.data.data.data.map(s => ({
                        id: s.id,
                        name: s.name
                    }))

                    // Fetch gaps for all sectionals
                    await this.fetchGapsForAllSectionals()
                }
            } catch (error) {
                this.allSectionals = []
            } finally {
                this.loading = false
            }
        },

        async fetchGapsForAllSectionals() {
            // Check cache first
            const cacheKey = this.selectedYear
            if (this.yearCache[cacheKey]) {
                this.sectionalGapsData = this.yearCache[cacheKey]
                return
            }

            this.loading = true
            this.sectionalGapsData = []

            try {
                // Filter sectionals if sectionalIds is specified
                const sectionalsToCheck = this.sectionalIds && this.sectionalIds.length > 0
                    ? this.allSectionals.filter(s => this.sectionalIds.includes(s.id))
                    : this.allSectionals

                // Fetch gaps for each sectional in parallel
                const gapsPromises = sectionalsToCheck.map(async (sectional) => {
                    try {
                        const data = await this.$store.dispatch('FETCH_GAPS', {
                            modelType: this.modelType,
                            sectionalId: sectional.id,
                            year: this.selectedYear
                        })

                        // Only include sectionals with gaps
                        if (data && data.enabled !== false && data.total_missing > 0) {
                            return {
                                sectionalId: sectional.id,
                                sectionalName: data.sectional_name || sectional.name,
                                totalMissing: data.total_missing,
                                range: data.range,
                                gaps: data.gaps || []
                            }
                        }
                    } catch (error) {
                        // Silently handle error for individual sectional
                    }
                    return null
                })

                const results = await Promise.all(gapsPromises)
                this.sectionalGapsData = results.filter(r => r !== null)

                // Cache the results
                this.yearCache[cacheKey] = this.sectionalGapsData
            } finally {
                this.loading = false
            }
        },

        formatGaps(gaps) {
            if (!gaps || gaps.length === 0) return ''

            // Group consecutive numbers
            const groups = []
            let start = gaps[0]
            let end = gaps[0]

            for (let i = 1; i < gaps.length; i++) {
                if (gaps[i] === end + 1) {
                    end = gaps[i]
                } else {
                    if (start === end) {
                        groups.push(start.toString())
                    } else {
                        groups.push(`${start}-${end}`)
                    }
                    start = gaps[i]
                    end = gaps[i]
                }
            }

            // Add last group
            if (start === end) {
                groups.push(start.toString())
            } else {
                groups.push(`${start}-${end}`)
            }

            return groups.join(', ')
        },

        toggleGapExpansion(key) {
            this.$set(this.expandedGaps, key, !this.expandedGaps[key])
        },

        async invalidateCache() {
            // Clear local cached data
            this.yearCache = {}

            // Also invalidate backend cache for all sectionals of current year
            const sectionalsToCheck = this.sectionalIds && this.sectionalIds.length > 0
                ? this.allSectionals.filter(s => this.sectionalIds.includes(s.id))
                : this.allSectionals

            // Invalidate backend cache for each sectional
            const invalidatePromises = sectionalsToCheck.map(sectional =>
                this.$store.dispatch('INVALIDATE_CACHE', {
                    modelType: this.modelType,
                    sectionalId: sectional.id,
                    year: this.selectedYear
                }).catch(() => {
                    // Silently handle cache invalidation error
                })
            )

            await Promise.all(invalidatePromises)
        },

        async refreshData() {
            // Clear both frontend and backend cache, then refetch
            await this.invalidateCache()
            await this.fetchGapsForAllSectionals()
        },

        handleDocumentsChanged(payload) {
            // Check if the changed documents are of the same type we're monitoring
            if (payload && payload.modelDef) {
                // Remove 'documents-' prefix if present
                let modelDefClean = payload.modelDef
                if (modelDefClean.startsWith('documents-')) {
                    modelDefClean = modelDefClean.replace('documents-', '')
                }

                // Map modelDef to modelType
                // proformainvoice -> proformainvoice, saleinvoice -> saleinvoice, etc.
                const modelMapping = {
                    'proformainvoice': 'proformainvoice',
                    'saleinvoice': 'saleinvoice',
                    'creditnote': 'creditnote',
                    'purchaseinvoice': 'purchaseinvoice',
                    'debitnote': 'debitnote',
                    'suppliercreditnote': 'suppliercreditnote',
                    'selfinvoice': 'selfinvoice',
                    'ddt': 'ddt',
                    'quotation': 'quotation',
                    'saleorder': 'saleorder'
                }

                const changedModelType = modelMapping[modelDefClean]

                if (changedModelType && changedModelType === this.modelType) {
                    // Refresh gaps data automatically
                    this.refreshData()
                }
            }
        }
    }
}
</script>

<style scoped>
.compact-gaps-alert >>> .v-alert__content {
    padding: 12px 0;
}

.compact-gaps-alert >>> .v-expansion-panel::before {
    box-shadow: none;
}

.compact-gaps-alert >>> .v-expansion-panel-header {
    min-height: 36px !important;
    padding: 4px 0;
}

.compact-gaps-alert >>> .v-expansion-panel-content__wrap {
    padding: 0;
}

/* Remove extra padding from expansion panels */
.compact-gaps-alert >>> .v-expansion-panels {
    margin-top: 0;
}

/* Responsive adjustments */
@media (max-width: 600px) {
    .compact-gaps-alert >>> .v-alert__content {
        padding: 8px 0;
    }
}
</style>
